/* ShiftMinimal — dynamic art glow.
   Samples the active product artwork on the PDP glass card and drives
   --sm-art-glow-rgb (card lights, ambient room glow, shirt halo).
   Glow layers stay hidden (.sm-art-glow-pending) until the first sample
   lands, then fade in with the extracted color — no cyan flash. */
(() => {
  const SAMPLE = 64;
  const HUE_BUCKET = 20; /* degrees */
  const cache = new Map();

  function rgbToHsl(r, g, b) {
    r /= 255;
    g /= 255;
    b /= 255;
    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    const l = (max + min) / 2;
    if (max === min) return [0, 0, l];
    const d = max - min;
    const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    let h;
    if (max === r) h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
    else if (max === g) h = ((b - r) / d + 2) / 6;
    else h = ((r - g) / d + 4) / 6;
    return [h, s, l];
  }

  function hslToRgb(h, s, l) {
    const hue = (p, q, t) => {
      if (t < 0) t += 1;
      if (t > 1) t -= 1;
      if (t < 1 / 6) return p + (q - p) * 6 * t;
      if (t < 1 / 2) return q;
      if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
      return p;
    };
    if (s === 0) {
      const v = Math.round(l * 255);
      return [v, v, v];
    }
    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;
    return [
      Math.round(hue(p, q, h + 1 / 3) * 255),
      Math.round(hue(p, q, h) * 255),
      Math.round(hue(p, q, h - 1 / 3) * 255),
    ];
  }

  async function extract(src) {
    if (cache.has(src)) return cache.get(src);
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.src = src;
    await img.decode();
    const canvas = document.createElement('canvas');
    canvas.width = SAMPLE;
    canvas.height = SAMPLE;
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    ctx.drawImage(img, 0, 0, SAMPLE, SAMPLE);
    const { data } = ctx.getImageData(0, 0, SAMPLE, SAMPLE);

    /* Bucket vibrant pixels by hue, weighted by saturation, so the tee
       body (near-black), skin tones and background greys don't win. */
    const buckets = new Map();
    for (let i = 0; i < data.length; i += 4) {
      const a = data[i + 3];
      if (a < 200) continue;
      const r = data[i];
      const g = data[i + 1];
      const b = data[i + 2];
      const [h, s, l] = rgbToHsl(r, g, b);
      if (s < 0.25 || l < 0.12 || l > 0.9) continue;
      /* Skip white/cream background pixels (Printrove JPG mockup bg) */
      if (r > 230 && g > 225 && b > 210) continue;
      /* Skip near-black tee-body pixels even at low alpha */
      if (r < 28 && g < 28 && b < 28) continue;
      const key = Math.round((h * 360) / HUE_BUCKET) * HUE_BUCKET;
      const e = buckets.get(key) || { w: 0, r: 0, g: 0, b: 0 };
      e.w += s;
      e.r += r * s;
      e.g += g * s;
      e.b += b * s;
      buckets.set(key, e);
    }

    let best = null;
    for (const e of buckets.values()) {
      if (!best || e.w > best.w) best = e;
    }
    let result = null;
    if (best) {
      /* Boost to glow range: saturated and light enough to read against
         near-black, but not neon. */
      const [h, s, l] = rgbToHsl(best.r / best.w, best.g / best.w, best.b / best.w);
      const boosted = hslToRgb(h, Math.max(s, 0.72), Math.min(Math.max(l, 0.56), 0.68));
      result = boosted.join(', ');
    }
    cache.set(src, result);
    return result;
  }

  function activeImage(scene) {
    return (
      scene.querySelector('slideshow-slide[aria-hidden="false"] img') ||
      scene.querySelector('img')
    );
  }

  function markReady(card, media) {
    card.classList.remove('sm-art-glow-pending');
    card.classList.add('sm-art-glow-ready');
    if (media) media.classList.add('sm-art-glow-ready');
  }

  function applyGlow(card, media, rgb) {
    card.style.setProperty('--sm-art-glow-rgb', rgb || '0, 0, 0');
    if (media) media.style.setProperty('--sm-art-glow-rgb', rgb || '0, 0, 0');
    markReady(card, media);
  }

  function init() {
    const media = document.querySelector('.sm-drop-artifact .product-information__media');
    const card = media && media.querySelector('.sm-float-card');
    const scene = card && card.querySelector('.sm-float-card__scene');
    if (!scene) return;

    let token = 0;
    let debounceTimer = null;
    let hasRevealed = false;

    const update = () => {
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => {
        const img = activeImage(scene);
        const src = img && (img.currentSrc || img.src);
        if (!src) return;
        /* Chroma-keying swaps the same artwork to a data URL. Keep the first
           sampled color instead of decoding and sampling the full image twice. */
        if (hasRevealed && src.startsWith('data:')) return;
        const mine = ++token;
        extract(src)
          .then((rgb) => {
            if (mine !== token) return;
            applyGlow(card, media, rgb || '0, 0, 0');
            hasRevealed = true;
          })
          .catch(() => {
            if (mine !== token) return;
            if (!hasRevealed) {
              /* CORS taint or no vibrant pixels — keep lights dark, only remove pending state */
              applyGlow(card, media, '0, 0, 0');
              hasRevealed = true;
            }
          });
      }, 48);
    };

    const img = activeImage(scene);
    if (img && !(img.complete && img.naturalWidth)) {
      img.addEventListener('load', update, { once: true });
    }
    update();

    /* Re-sample when the slideshow advances or a variant swaps the image. */
    new MutationObserver(update).observe(scene, {
      subtree: true,
      attributes: true,
      attributeFilter: ['aria-hidden', 'src', 'srcset'],
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
